import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { fmtDate } from '../lib/dates';

import {
  buildEstate, sortEstateRows, expiringWindows, coverageByDocument, plural,
} from '../lib/complianceEstate';

// Compliance Overview — the Compliance page's second tab.
//
// THE QUEUE IS FOR DOING; THIS TAB IS FOR KNOWING. Every per-document action
// (approve, view file, request clarification, reject) lives on the Review
// Queue. This tab answers "what is the state of the estate, and where is the
// risk", and deep-links into the queue for the doing.
//
// Sections: At a Glance (tiles + distribution) · Suppliers (the filterable
// list the tiles drive) · Expiring soon (7/30/60/90, forward-looking) ·
// Coverage (per document type) · Chase (confirmed batch reminders) · Blocked
// on compliance (open briefs with un-awardable recipients) · Recent activity.
//
// Reshaped 2026-08-14. It previously carried its own Awaiting Review,
// Expiring Soon and Expired lists with per-document Approve / View file /
// Dismiss — all three fully duplicated by the Review Queue, and two screens
// approving the same document is two places for it to be approved twice.
//
// lib/complianceEstate owns every derivation; this file only renders. It
// deliberately does NOT recompute a supplier’s colour: the database decides
// that, and it gates awarding.

const fmtRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) {
    // Within a day — show "Yesterday, HH:mm" once we crossed midnight.
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (then < today.getTime()) return `Yesterday, ${new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    return `${hr} hr ago`;
  }
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day} day${day === 1 ? '' : 's'} ago`;
  return fmtDate(iso);
};

export default function ComplianceOverview({ suppliers, currentInternalUser, docLabelMap, onGoToQueue, navigate }) {
  const [docs, setDocs] = useState(null); // null = loading
  const [audit, setAudit] = useState([]);
  const [toast, setToast] = useState(null);
  // Estate inputs — what's required of whom, and the evidence for it.
  const [requirements, setRequirements] = useState([]);
  const [liveDocs, setLiveDocs] = useState([]);
  const [acknowledgements, setAcknowledgements] = useState([]);
  const [loadError, setLoadError] = useState(null);
  // Supplier list (§2): which tile is filtering it, and the name search.
  const [stateFilter, setStateFilter] = useState(null); // null = all
  const [supplierSearch, setSupplierSearch] = useState('');
  // Bulk chase (§6). `chaseIntent` holds a pending send until it's confirmed —
  // nothing goes out without the confirmation naming who and what.
  const [chaseIntent, setChaseIntent] = useState(null);
  const [chasing, setChasing] = useState(false);
  // Open briefs with non-compliant recipients (§7).
  const [openBriefs, setOpenBriefs] = useState([]);

  // Compliance tracking only applies to active suppliers — inactive /
  // record-only suppliers don't need chasing. supplierById keeps the
  // full set for audit-log name lookups (an entry from a since-
  // deactivated supplier still deserves a name), while
  // activeSupplierIdSet drives which suppliers feed the tile counts
  // and which onboarding docs land in the three queues below.
  const supplierById = useMemo(() => {
    const map = new Map();
    for (const s of suppliers || []) map.set(s.id, s);
    return map;
  }, [suppliers]);
  const activeSupplierIdSet = useMemo(() => {
    const set = new Set();
    for (const s of suppliers || []) {
      if (s.status === 'active') set.add(s.id);
    }
    return set;
  }, [suppliers]);
  const activeSuppliers = useMemo(
    () => (suppliers || []).filter(s => s.status === 'active'),
    [suppliers]
  );

  const labelFor = (key) => docLabelMap?.get?.(key) || key;

  const loadAll = async () => {
    // All onboarding docs + the audit log. Both datasets are small at the
    // company's scale — fetching whole and bucketing client-side is simpler
    // than running separate server-side queries.
    //
    // ⚠️ not_required rows are now LOADED, not filtered out at the database.
    // The estate builder has to SUBTRACT a waiver from the requirement set, so
    // a filtered-out waiver reads as an outstanding document — the supplier
    // would show red for something Fusion explicitly said they don't need. The
    // three legacy queues below still skip them; that filtering moved into the
    // bucketing where it belongs.
    //
    // The other three reads are what turns "11 red" into "11 red, and here is
    // who they are and why": requirements per risk tier, the live document
    // masters those requirements point at, and acknowledgements (a document
    // satisfied by signature never touches an onboarding row at all).
    const [docsRes, auditRes, reqRes, liveDocsRes, acksRes, briefsRes] = await Promise.all([
      supabase
        .from('supplier_onboarding_documents')
        .select('id, supplier_id, document_key, expiry_date, expiry_approved_at, supplier_uploaded_at, supplier_uploaded_file_url, supplier_uploaded_file_name, expiry_warning_email_sent_at, expiry_email_sent_at, expiry_warning_acknowledged_at, expiry_warning_acknowledged_by, not_required, risk_override_applicable, review_decision, signed_at')
        .order('supplier_uploaded_at', { ascending: false }),
      supabase
        .from('compliance_audit_log')
        .select('id, ts, actor_id, action, supplier_id, document_key, meta, actor:internal_users!actor_id(id, full_name)')
        .order('ts', { ascending: false })
        .limit(200),
      supabase.from('risk_document_requirements').select('risk_level, document_key'),
      supabase.from('fusion_documents')
        .select('id, compliance_doc_key, document_name, satisfied_by, is_active, status, version_value'),
      supabase.from('document_acknowledgements')
        .select('id, supplier_id, document_key, acknowledged_at, reviewed_at, review_decision, document_version'),
      // §7 — briefs still open, with who they went to. A closed or archived
      // brief can't be blocked by anything.
      supabase.from('briefs')
        .select('id, brief_name, fusion_project_number, status, archived_at, brief_recipients(supplier_id)')
        .in('status', ['sent', 'open', 'draft'])
        .is('archived_at', null),
    ]);
    // Errors are surfaced rather than swallowed. An empty estate list caused by
    // a failed read looks exactly like a compliant estate, and this tab's whole
    // job is telling you it isn't one.
    const failed = [
      ['documents', docsRes], ['requirements', reqRes],
      ['document library', liveDocsRes], ['acknowledgements', acksRes],
    ].filter(([, r]) => r?.error).map(([name]) => name);
    setLoadError(failed.length ? `Couldn't load ${failed.join(', ')} — the numbers below are incomplete.` : null);
    setDocs(docsRes.data || []);
    setAudit(auditRes.data || []);
    setRequirements(reqRes.data || []);
    setLiveDocs(liveDocsRes.data || []);
    setAcknowledgements(acksRes.data || []);
    // Briefs are the softest of the reads — if they fail, the consequence
    // section simply doesn't render. Not worth a red banner over the estate.
    setOpenBriefs(briefsRes.data || []);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // How many times each SUPPLIER has been chased, from the audit log.
  //
  // The deleted Expired section carried a "No response" pill once a document
  // had been chased three times, and that signal is worth more than the list it
  // sat in: a supplier who has been asked four times and still hasn't sent
  // anything is a different problem from one who was asked yesterday, and
  // chasing them a fifth time is not the answer. It moves onto the supplier row.
  //
  // Counted per supplier rather than per document, because the row is the
  // supplier — and it's the same audit table the bulk chase writes to, so a
  // batch send raises this exactly like a manual one did.
  const chaseCounts = useMemo(() => {
    const m = new Map();
    for (const a of audit) {
      if (a.action !== 'reminder_sent') continue;
      m.set(a.supplier_id, (m.get(a.supplier_id) || 0) + 1);
    }
    return m;
  }, [audit]);
  const NO_RESPONSE_THRESHOLD = 3;

  // Documents the supplier has submitted and nobody has decided on yet.
  //
  // A COUNT ONLY — it drives the link into the Review Queue, which is where
  // these are actually worked. The expiring and expired buckets that used to sit
  // alongside it went with their lists: the forward view is derived from the
  // estate now (lib/complianceEstate), and the lapsed ones are in the red tile's
  // breakdown and in the queue's own Expired pill.
  const awaiting = useMemo(() => {
    if (!docs) return [];
    return docs.filter(d =>
      !d.not_required
      // Compliance posture isn't meaningful for suppliers we're not briefing.
      && activeSupplierIdSet.has(d.supplier_id)
      && d.supplier_uploaded_at && !d.expiry_approved_at);
  }, [docs, activeSupplierIdSet]);

  // ----- Toast helper — matches the codebase pattern (no real toast
  // component exists; setError-style auto-clearing line). Kept here so
  // the section interactions can fire short success messages without
  // dragging in a new abstraction. Optional `action` renders an inline
  // button (e.g. "Undo") next to the message; the action's onClick
  // fires before the auto-clear so the click handler can override.
  const flash = (msg, tone = 'green', action = null) => {
    setToast({ msg, tone, action });
    setTimeout(() => setToast(prev => (prev && prev.msg === msg ? null : prev)), action ? 5500 : 3500);
  };

  // ----- Action handlers ----------------------------------------------------

  // handleApprove + maybeAwardBadge were deleted 2026-08-14 with the Awaiting
  // Review section (§1). Approving a document is the Review Queue tab's job,
  // and two screens approving the same document is two places for it to be
  // approved twice. Badge awarding is NOT lost — AdminComplianceReview already
  // calls recomputeEcoBadge on its own approve and reject paths.
  // The per-list search, sort and supplier-grouping helpers went with the three
  // lists they served. The one list left — Suppliers — sorts through
  // lib/complianceEstate, so its order is the same one the estate itself
  // defines rather than a second opinion held here.

  // ----- Status tiles + proportions bar -------------------------------------

  // The estate — one row per active supplier, with the document driving its
  // state. lib/complianceEstate owns the derivation; this component only
  // renders it. See that module for why the DATABASE keeps the last word on a
  // supplier's colour and nothing here recomputes it.
  const estate = useMemo(() => buildEstate({
    suppliers: activeSuppliers,
    requirements,
    liveDocs,
    records: docs || [],
    acknowledgements,
  }), [activeSuppliers, requirements, liveDocs, docs, acknowledgements]);

  const buckets = useMemo(() => {
    const out = { green: [], amber: [], red: [], not_set: [] };
    for (const row of estate.rows) out[row.state].push(row);
    return out;
  }, [estate]);
  const totalSuppliers = activeSuppliers.length;

  // §8 — captions are conditional on the value. The old ones were static text
  // on dynamic tiles, so the NOT SET tile read "0 · Major backlog".
  //
  // §4 — the red tile carries a two-part breakdown in DOCUMENTS. Deliberately
  // not two supplier-count tiles: a supplier holding one expired document and
  // one never-supplied would appear in both, and two tiles that double-count
  // are worse than one that merges. Document counts sum cleanly.
  //
  // Investigated 2026-08-14: the split is the point of this tab. Red is
  // returned by the same bool_or for "never supplied" and "lapsed last week",
  // and those want completely different conversations.
  const t = estate.totals;
  const redBreakdown = [
    t.expiredDocs       ? `${plural(t.expiredDocs, 'expired')}` : null,
    t.neverSuppliedDocs ? `${t.neverSuppliedDocs} never supplied` : null,
    t.rejectedDocs      ? `${t.rejectedDocs} rejected` : null,
  ].filter(Boolean).join(' · ');

  const tiles = [
    {
      key: 'green', label: 'Green', count: buckets.green.length, color: '#92D400',
      meaning: buckets.green.length === 0 ? 'No supplier is fully current.'
        : buckets.green.length === totalSuppliers ? 'Every supplier is current. Nothing to chase.'
        : 'All documents current. No action needed.',
    },
    {
      key: 'amber', label: 'Amber', count: buckets.amber.length, color: '#E89A2E',
      meaning: buckets.amber.length === 0 ? 'Nothing expiring or awaiting review.'
        : 'Expiring within 30 days, or waiting on a review. Chase soon.',
    },
    {
      key: 'red', label: 'Red', count: buckets.red.length, color: '#E0290A',
      meaning: buckets.red.length === 0 ? 'No supplier is blocked from award.'
        : 'Cannot award these suppliers.',
      // The honest breakdown, in documents. Named as documents in the render so
      // the unit can't be misread as suppliers.
      breakdown: redBreakdown || null,
    },
    // ⚠️ NOT SET is rendered ONLY when it's non-zero, and it is normally zero
    // by construction: compute_supplier_compliance_status returns red, amber or
    // green and nothing else, and compliance_status is NULLed in exactly one
    // case — the supplier isn't active — while this list is active suppliers
    // only. A permanently empty tile taking a quarter of the row is what §8 is
    // really about; rewording it would have left the hole.
    //
    // It CAN light up: an active supplier that has never been through a
    // recompute is born NULL. That's a real state and worth seeing, which is
    // why the tile stays in the array rather than being deleted.
    ...(buckets.not_set.length > 0 ? [{
      key: 'not_set', label: 'Not set', count: buckets.not_set.length, color: '#51534A',
      meaning: 'Never assessed — these suppliers need a compliance recompute.',
    }] : []),
  ];

  // §2 — the list the tiles have always claimed to filter. Worst state first,
  // then soonest expiry, then name (lib/complianceEstate owns the order).
  const supplierRows = useMemo(() => {
    const q = supplierSearch.trim().toLowerCase();
    let rows = estate.rows;
    if (stateFilter) rows = rows.filter(r => r.state === stateFilter);
    if (q) rows = rows.filter(r => r.name.toLowerCase().includes(q));
    return sortEstateRows(rows);
  }, [estate, stateFilter, supplierSearch]);

  // §3 — the forward view. Everything here is CURRENT; these are the documents
  // that become next month's red if nobody chases them.
  const windows = useMemo(() => expiringWindows(estate.rows), [estate]);
  const windowsTotal = windows.reduce((n, w) => n + w.docs.length, 0);

  // §5 — coverage. The denominator is per risk tier, not the supplier count.
  const coverage = useMemo(() => coverageByDocument(estate.rows), [estate]);

  // §6 — who a bulk reminder would actually reach.
  const amberRows = useMemo(() => estate.rows.filter(r => r.state === 'amber'), [estate]);

  // §7 — open briefs carrying a recipient who can't be awarded. Derived from
  // the estate's own red set rather than re-reading compliance_status, so the
  // count here and the red tile can't disagree.
  const blockedBriefs = useMemo(() => {
    const redIds = new Set(estate.rows.filter(r => r.state === 'red').map(r => r.supplierId));
    if (redIds.size === 0) return [];
    const nameById = new Map(estate.rows.map(r => [r.supplierId, r.name]));
    return openBriefs
      .map(b => {
        const recipients = b.brief_recipients || [];
        const blocked = recipients.filter(r => redIds.has(r.supplier_id));
        return {
          id: b.id,
          name: b.brief_name || b.fusion_project_number || 'Brief',
          recipientCount: recipients.length,
          blockedNames: blocked.map(r => nameById.get(r.supplier_id) || 'Supplier'),
        };
      })
      .filter(b => b.blockedNames.length > 0)
      .sort((a, b) => b.blockedNames.length - a.blockedNames.length);
  }, [estate, openBriefs]);

  // Bulk chase. Stamps the same reminder columns and writes the same
  // compliance_audit_log rows the per-supplier reminder used to, so batch sends
  // appear in the activity feed and in the reminder counts exactly like manual
  // ones — a chase that leaves no trace is how a supplier gets asked four times.
  const runChase = async (intent) => {
    if (!intent || chasing) return;
    setChasing(true);
    const now = new Date().toISOString();

    // Which documents each supplier is actually being chased for. An amber
    // chase covers everything they owe; a document chase covers exactly one.
    const targets = [];
    for (const row of intent.rows) {
      const entries = intent.kind === 'document'
        ? row.entries.filter(e => e.key === intent.documentKey)
        : row.entries.filter(e => e.driver !== 'approved' && e.driver !== 'not_required');
      for (const e of entries) targets.push({ row, entry: e });
    }

    const auditRows = targets.map(({ row, entry }) => ({
      actor_id: currentInternalUser?.id || null,
      action: 'reminder_sent',
      supplier_id: row.supplierId,
      document_key: entry.key,
      meta: { ruleApplied: 'manual', bulk: true, from: 'compliance_overview', chase: intent.kind },
    }));

    // Stamp the reminder columns on the rows that have one. A document the
    // supplier has never touched has no onboarding row to stamp — the audit
    // entry above is the record for those.
    const stampable = targets.filter(({ entry }) => entry.raw?.id);
    const results = await Promise.all(stampable.map(({ entry }) => {
      const lapsed = entry.expiryDate && entry.daysToExpiry != null && entry.daysToExpiry < 0;
      const column = lapsed ? 'expiry_email_sent_at' : 'expiry_warning_email_sent_at';
      return supabase.from('supplier_onboarding_documents')
        .update({ [column]: now, updated_at: now }).eq('id', entry.raw.id);
    }));
    const failed = results.filter(r => r.error).length;

    let auditErr = null;
    if (auditRows.length) {
      ({ error: auditErr } = await supabase.from('compliance_audit_log').insert(auditRows));
    }

    setChasing(false);
    setChaseIntent(null);
    if (failed > 0 || auditErr) {
      flash(`Chase partly failed — ${failed} of ${stampable.length} documents not stamped${auditErr ? ', and the audit entry did not save' : ''}.`, 'red');
    } else {
      flash(`${plural(intent.rows.length, 'supplier')} chased · ${plural(targets.length, 'document')}`);
    }
    await loadAll();
  };

  // The state chip. Colour is NOT the only signal — each state has its own
  // glyph and its own word, so the list reads in greyscale and to anyone who
  // can't separate the red from the green.
  const STATE_CHIP = {
    red:     { label: 'Red',     glyph: '▲', cls: 'text-[#9c2b12] bg-[#E0290A]/[0.10] border-[#E0290A]/30' },
    amber:   { label: 'Amber',   glyph: '●', cls: 'text-[#7A4E10] bg-[#E89A2E]/[0.12] border-[#E89A2E]/40' },
    green:   { label: 'Green',   glyph: '✓', cls: 'text-fusion-forest bg-[#92D400]/[0.12] border-[#92D400]/40' },
    not_set: { label: 'Not set', glyph: '–', cls: 'text-[#51534A] bg-[#51534A]/[0.10] border-[#DADCCD]' },
  };

  // ----- Render ------------------------------------------------------------

  const sectionHeader = (label, sub, count, countTone) => (
    <div className="flex items-center gap-4 mt-10 mb-4">
      <p className="font-['Metropolis'] text-[11px] font-bold tracking-[3px] uppercase text-fusion-forest">{label}</p>
      {count != null && (
        <span className={`font-['Metropolis'] text-[10px] font-bold tracking-[2px] uppercase rounded-full px-2.5 py-0.5 border ${
          countTone === 'amber'   ? 'bg-[#E89A2E]/10 text-[#7A4E10] border-[#E89A2E]/30' :
          countTone === 'red'     ? 'bg-[#E0290A]/10 text-[#E0290A] border-[#E0290A]/30' :
          countTone === 'green'   ? 'bg-[#92D400]/10 text-fusion-forest border-[#92D400]/30' :
          countTone === 'not_set' ? 'bg-[#51534A]/10 text-[#51534A] border-[#DADCCD]' :
                                  'bg-[#51534A]/10 text-[#51534A] border-[#DADCCD]/30'
        }`}>{count}</span>
      )}
      <span className="flex-1 h-px bg-white" />
      <span className="font-['Metropolis'] text-[11px] font-semibold text-[#51534A]">{sub}</span>
    </div>
  );

  return (
    <div className="font-['Metropolis']">

      {/* Toast — matches the existing setError-style inline banner.
          Optional inline action button (Undo, etc.) renders to the
          right of the message; clicking it dismisses the toast and
          fires the action's onClick. */}
      {toast && (
        <div className={`mb-4 rounded-xl px-4 py-2.5 border text-xs font-semibold tracking-[1px] uppercase flex items-center justify-between gap-3 ${
          toast.tone === 'red' ? 'bg-[#E0290A]/10 border-[#E0290A]/30 text-[#E0290A]' :
                                 'bg-[#92D400]/10 border-[#92D400]/30 text-fusion-forest'
        }`}>
          <span>{toast.msg}</span>
          {toast.action && (
            <button
              type="button"
              onClick={() => { setToast(null); toast.action.onClick?.(); }}
              className="bg-transparent border border-current rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-[1.5px] uppercase hover:bg-current/10 transition-colors">
              {toast.action.label}
            </button>
          )}
        </div>
      )}

      {/* A failed read makes the estate look compliant, which is the one thing
          this tab must never do silently. */}
      {loadError && (
        <div className="mb-4 rounded-xl px-4 py-2.5 border bg-[#E0290A]/10 border-[#E0290A]/30 text-[#E0290A] text-xs font-semibold">
          {loadError}
        </div>
      )}

      {/* ===== SECTION 1: At a Glance ===== */}
      <div className="flex items-center gap-4 mb-4">
        <p className="font-['Metropolis'] text-[11px] font-bold tracking-[3px] uppercase text-fusion-forest">At a Glance</p>
        <span className="flex-1 h-px bg-white" />
        <span className="font-['Metropolis'] text-[11px] font-semibold text-[#51534A]">Click any tile to filter suppliers by compliance state</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {tiles.map(t => {
          const active = stateFilter === t.key;
          return (
            <button
              key={t.key}
              type="button"
              aria-pressed={active}
              // Filters the list below rather than navigating away. The caption
              // has promised this since the tiles were built; there was simply
              // no list on the tab to filter. Clicking the active tile clears.
              onClick={() => setStateFilter(active ? null : t.key)}
              className={`bg-white border rounded-xl px-4 py-4 text-left hover:opacity-85 transition-opacity relative overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest ${
                active ? 'border-fusion-forest ring-1 ring-fusion-forest' : 'border-line'
              }`}>
              <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: t.color }} />
              <div className="flex items-center gap-2 mb-2 pl-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
                <p className="text-[10px] font-bold tracking-[2px] uppercase text-[#51534A]">{t.label}</p>
                {active && <span className="ml-auto text-[9px] font-bold tracking-[1.5px] uppercase text-fusion-forest">Filtering</span>}
              </div>
              <p className="text-4xl font-extrabold leading-none mb-2 pl-1" style={{ color: t.color }}>{t.count}</p>
              <p className="text-[11px] text-fusion-mid-grey leading-relaxed pl-1">{t.meaning}</p>
              {/* §4 — the split, in documents. The unit is named because the
                  number above it is suppliers, and a bare "9 · 2" under a "11"
                  invites exactly the wrong reading. */}
              {t.breakdown && (
                <p className="text-[11px] font-semibold text-[#9c2b12] leading-relaxed pl-1 mt-1.5 pt-1.5 border-t border-[#E0290A]/20">
                  {t.breakdown}
                  <span className="block font-normal text-[10px] text-fusion-mid-grey mt-0.5">documents</span>
                </p>
              )}
            </button>
          );
        })}
      </div>

      {/* Proportions bar */}
      {totalSuppliers > 0 && (
        <div className="bg-white border border-line rounded-xl px-4 py-3 mb-4">
          <div className="flex h-2 rounded-full overflow-hidden bg-white/[0.04]">
            {tiles.map(t => {
              const pct = (t.count / totalSuppliers) * 100;
              if (pct === 0) return null;
              return <span key={t.key} style={{ width: `${pct}%`, background: t.color }} title={`${t.label} · ${t.count}`} />;
            })}
          </div>
          <div className="flex items-center gap-3 mt-2.5 flex-wrap">
            {tiles.map(t => (
              <span key={t.key} className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[1px] uppercase text-[#51534A]">
                <span className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                {t.label} · {Math.round((t.count / totalSuppliers) * 100)}%
              </span>
            ))}
            <span className="ml-auto text-[10px] font-semibold tracking-[1px] uppercase text-[#51534A]">
              {totalSuppliers} suppliers total
            </span>
          </div>
        </div>
      )}

      {/* §1 — the Awaiting Review list used to live here in full: search, sort,
          per-supplier cards, View file, Approve. Every one of those is the
          Review Queue tab's job, and two places to approve the same document is
          two places for it to be approved twice. All that's left is the count
          and a way through.
          The backlog banner that also sat here has gone with it. It rendered
          only when Not Set exceeded 20% of suppliers, and Not Set is normally
          zero by construction — so it was unreachable code that had never once
          been seen. */}
      {awaiting.length > 0 && (
        <button
          type="button"
          onClick={() => onGoToQueue?.()}
          className="w-full bg-white border border-[#E89A2E]/40 rounded-xl px-4 py-3 mb-4 flex items-center justify-between gap-3 text-left hover:border-[#E89A2E] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
          <span className="text-xs text-fusion-mid-grey leading-relaxed">
            <strong className="text-[#7A4E10]">{plural(awaiting.length, 'document')}</strong> awaiting your review.
          </span>
          <span className="font-['Metropolis'] text-xs font-bold tracking-[2px] uppercase text-[#7A4E10] flex-shrink-0">
            Review queue →
          </span>
        </button>
      )}

      {/* ===== SECTION 2: The suppliers ===== */}
      {/* §2 — the list the tiles have promised to filter since they were built.
          Eleven suppliers were red and there was no way to learn who from this
          tab. Rows carry the document DRIVING the state, because "red" on its
          own tells nobody what to do about it. */}
      {sectionHeader(
        'Suppliers',
        stateFilter ? `Filtered · ${STATE_CHIP[stateFilter].label}` : 'Worst first, then soonest expiry',
        supplierRows.length,
        stateFilter || 'grey',
      )}

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          type="text"
          value={supplierSearch}
          onChange={(e) => setSupplierSearch(e.target.value)}
          placeholder="Search supplier…"
          aria-label="Search suppliers by name"
          className="flex-1 min-w-[240px] bg-white border border-line rounded-xl text-ink px-3.5 py-2 text-sm focus:outline-none focus:border-[#92D400]" />
        {(stateFilter || supplierSearch) && (
          <button
            type="button"
            onClick={() => { setStateFilter(null); setSupplierSearch(''); }}
            className="bg-transparent border border-line rounded-xl text-[#51534A] text-xs font-semibold px-3 py-2 hover:text-ink hover:border-[#DADCCD] transition-colors">
            Clear filter
          </button>
        )}
      </div>

      {docs === null ? (
        <p className="text-sm text-[#51534A] italic py-4">Loading suppliers…</p>
      ) : supplierRows.length === 0 ? (
        <p className="text-sm text-[#51534A] italic py-4">
          {estate.rows.length === 0
            ? 'No active suppliers yet.'
            : 'No suppliers match that filter.'}
        </p>
      ) : (
        <div className="bg-white border border-line rounded-xl overflow-hidden">
          {supplierRows.map(row => {
            const chip = STATE_CHIP[row.state];
            return (
              <div key={row.supplierId} className="flex items-center gap-4 px-4 py-3 border-b border-line last:border-b-0 flex-wrap">
                {/* Colour is never the only signal: glyph + word carry the
                    state on their own, so the list reads in greyscale. */}
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[1.5px] uppercase rounded-full border px-2.5 py-1 flex-shrink-0 ${chip.cls}`}>
                  <span aria-hidden="true">{chip.glyph}</span>{chip.label}
                </span>
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm font-bold text-ink truncate">{row.name}</p>
                  <p className="text-[11px] text-[#51534A] mt-0.5">
                    {row.driver
                      ? <>{row.driverLabel} · <span className="text-fusion-mid-grey">{row.driver.label}</span></>
                      : 'Nothing required of this supplier yet.'}
                  </p>
                </div>
                <div className="text-[11px] text-[#51534A] flex-shrink-0 whitespace-nowrap min-w-[120px]">
                  {row.relevantDate && (
                    <>
                      <span className="text-fusion-mid-grey font-semibold uppercase tracking-[1px] text-[10px]">
                        {row.relevantDateKind === 'expiry' ? 'Expires' : 'Assessed'}
                      </span>{' '}
                      {fmtDate(row.relevantDate)}
                    </>
                  )}
                </div>
                {/* Chased repeatedly and still outstanding. A fifth reminder
                    isn't the answer to this — someone needs to ring them. */}
                {row.state !== 'green' && (chaseCounts.get(row.supplierId) || 0) >= NO_RESPONSE_THRESHOLD && (
                  <span
                    title={`Chased ${plural(chaseCounts.get(row.supplierId), 'time')} with nothing supplied.`}
                    className="text-[10px] font-bold tracking-[1.5px] uppercase rounded-full px-2 py-0.5 bg-[#E0290A]/10 text-[#9c2b12] border border-[#E0290A]/30 flex-shrink-0">
                    No response
                  </span>
                )}
                {/* Evidence and database disagree. Shown rather than hidden:
                    lib/complianceEstate deliberately never overrules the
                    supplier row, so the honest thing is to say the two don't
                    match rather than to quietly pick one. */}
                {!row.stateMatchesEvidence && (
                  <span
                    title={`The documents suggest ${row.evidenceState}, but the supplier record says ${row.state}. A compliance recompute will settle it.`}
                    className="text-[10px] font-bold tracking-[1.5px] uppercase rounded-full px-2 py-0.5 bg-[#E89A2E]/10 text-[#7A4E10] border border-[#E89A2E]/30 flex-shrink-0">
                    Out of step
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => navigate?.(`/suppliers/${row.supplierId}?tab=compliance`)}
                  className="bg-white border border-line rounded-xl text-fusion-mid-grey text-xs font-semibold px-3 py-1.5 hover:border-[#DADCCD] transition-colors whitespace-nowrap flex-shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
                  Open supplier
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== SECTION 2b: Expiring soon — the forward view ===== */}
      {/* §3 — the Review Queue only shows what suppliers have already sent.
          Nothing surfaced a document about to lapse with no replacement in,
          which is how today's amber becomes next month's red.
          NOTE: the compliance state machine goes amber at exactly 30 days, so
          everything in the 60 and 90 buckets is still GREEN on the tiles. That
          is the point of the section, and the copy must not imply otherwise. */}
      {sectionHeader('Expiring soon', 'Current documents with a lapse date ahead of them', windowsTotal, windowsTotal > 0 ? 'amber' : 'green')}
      {docs === null ? null : windowsTotal === 0 ? (
        <p className="text-sm text-[#51534A] italic py-4">Nothing current expires in the next 90 days.</p>
      ) : (
        <div className="space-y-3">
          {windows.filter(w => w.docs.length > 0).map(w => (
            <div key={w.days} className="bg-white border border-line rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-line bg-[#FBFCF6] flex items-baseline justify-between gap-3">
                <p className="text-[11px] font-bold tracking-[2px] uppercase text-fusion-forest">
                  Within {w.days} days
                </p>
                <p className="text-[11px] text-[#51534A]">
                  {plural(w.docs.length, 'document')}
                  {w.days > 30 && <span className="text-[#9BA08C]"> · still green</span>}
                </p>
              </div>
              <div className="divide-y divide-line">
                {w.docs.map(d => (
                  <button
                    key={`${d.row.supplierId}:${d.key}`}
                    type="button"
                    onClick={() => navigate?.(`/suppliers/${d.row.supplierId}?tab=compliance`)}
                    className="w-full text-left flex items-center gap-4 px-4 py-2.5 hover:bg-[#F7F9F0]/60 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-fusion-forest">
                    <span className="text-sm text-ink font-semibold flex-1 min-w-0 truncate">{d.row.name}</span>
                    <span className="text-xs text-fusion-mid-grey flex-1 min-w-0 truncate">{d.label}</span>
                    <span className="text-[11px] text-[#51534A] whitespace-nowrap flex-shrink-0">
                      {fmtDate(d.expiryDate)} · {d.daysToExpiry === 0 ? 'today' : `in ${plural(d.daysToExpiry, 'day')}`}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== SECTION 2c: Coverage by document type ===== */}
      {/* §5 — distinguishes a supplier problem from a document-type problem. If
          most suppliers are missing the same document that's a process gap, not
          sixteen careless suppliers.
          The denominator is per RISK TIER, not the supplier count: a document
          only asked of high-risk suppliers reads 4 / 4, never 4 / 16. */}
      {coverage.length > 0 && (
        <>
          {sectionHeader('Coverage', 'How many suppliers hold each document, of those it is required of', null)}
          <div className="bg-white border border-line rounded-xl overflow-hidden">
            {coverage.map(c => {
              const pct = c.required ? Math.round((c.held / c.required) * 100) : 0;
              const short = c.required - c.held;
              return (
                <div key={c.key} className="flex items-center gap-4 px-4 py-2.5 border-b border-line last:border-b-0">
                  <span className="text-sm text-ink flex-1 min-w-0 truncate">{c.label}</span>
                  <div className="w-24 h-1.5 rounded-full bg-[#EEF1E6] overflow-hidden flex-shrink-0" aria-hidden="true">
                    <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: pct === 100 ? '#92D400' : pct >= 50 ? '#E89A2E' : '#E0290A' }} />
                  </div>
                  <span className="text-xs font-bold text-ink tabular-nums whitespace-nowrap flex-shrink-0 w-16 text-right">
                    {c.held} / {c.required}
                  </span>
                  <span className="text-[11px] text-[#51534A] whitespace-nowrap flex-shrink-0 w-28 text-right">
                    {short === 0 ? 'all current' : `${short} outstanding`}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}


      {/* The legacy Expiring Soon and Expired sections stood here. Both were
          fully duplicated by the Review Queue, which already has expired,
          expiring-soon, never-sent, rejected and needs-clarification pills — so
          they were the same fault as the Awaiting Review list, twice more.
          Their per-row "dismiss this warning" went with them, and that is worth
          recording: nothing else in the product ever read
          expiry_warning_acknowledged_at. It did not quiet the reminder cron and
          it did not affect the queue. It only hid the row from these two lists,
          so dismissing read as "handled" while the supplier carried on being
          chased. Removing the lists makes the flag vestigial rather than
          removing a capability. */}

      {/* ===== SECTION 3: Chase ===== */}
      {/* §6 — per-item actions belong in the queue; BATCH actions belong here.
          Every send names exactly who it reaches and what it asks for before it
          goes, and lands in compliance_audit_log like every other action. */}
      {(amberRows.length > 0 || coverage.some(c => c.missingSupplierIds.length > 0)) && (
        <>
          {sectionHeader('Chase', 'Batch reminders — always confirmed before sending', null)}
          <div className="bg-white border border-line rounded-xl px-4 py-3.5 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs text-fusion-mid-grey leading-relaxed flex-1 min-w-[220px]">
              {amberRows.length > 0
                ? <>{plural(amberRows.length, 'supplier is', 'suppliers are')} amber — expiring within 30 days, or waiting on a review.</>
                : <>No supplier is amber right now.</>}
            </p>
            <button
              type="button"
              disabled={amberRows.length === 0 || chasing}
              onClick={() => setChaseIntent({ kind: 'amber', rows: amberRows })}
              className="bg-[#92D400] text-[#131313] rounded-xl text-xs font-bold px-3.5 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-40 whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
              Remind all amber suppliers
            </button>
          </div>

          {/* Chase by document type, from the coverage list — this is the one
              that turns "six suppliers are missing the H&S policy" into an
              action rather than an observation. */}
          {coverage.filter(c => c.missingSupplierIds.length > 0).length > 0 && (
            <div className="bg-white border border-line rounded-xl overflow-hidden mt-3">
              {coverage.filter(c => c.missingSupplierIds.length > 0).map(c => (
                <div key={c.key} className="flex items-center gap-4 px-4 py-2.5 border-b border-line last:border-b-0">
                  <span className="text-sm text-ink flex-1 min-w-0 truncate">{c.label}</span>
                  <span className="text-[11px] text-[#51534A] whitespace-nowrap">
                    {plural(c.missingSupplierIds.length, 'supplier')} outstanding
                  </span>
                  <button
                    type="button"
                    disabled={chasing}
                    onClick={() => setChaseIntent({
                      kind: 'document',
                      documentKey: c.key,
                      documentLabel: c.label,
                      rows: estate.rows.filter(r => c.missingSupplierIds.includes(r.supplierId)),
                    })}
                    className="bg-white border border-line rounded-xl text-fusion-mid-grey text-xs font-semibold px-3 py-1.5 hover:border-[#DADCCD] transition-colors whitespace-nowrap flex-shrink-0 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
                    Chase
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Confirmation. NEVER sends silently: names the number of suppliers and
          the document being asked for, both, before anything goes out. */}
      {chaseIntent && (
        <div role="dialog" aria-modal="true" aria-label="Confirm chase"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#131313]/40">
          <div className="bg-white border border-line rounded-2xl max-w-md w-full p-5 shadow-lift">
            <p className="font-['Metropolis'] text-[15px] font-bold text-ink mb-2">
              {chaseIntent.kind === 'amber'
                ? `Remind ${plural(chaseIntent.rows.length, 'supplier')}?`
                : `Chase ${plural(chaseIntent.rows.length, 'supplier')} for ${chaseIntent.documentLabel}?`}
            </p>
            <p className="font-['Metropolis'] text-[12px] text-[#51534A] leading-relaxed mb-3">
              {chaseIntent.kind === 'amber'
                ? 'One email each, covering whatever that supplier owes. Suppliers with nothing outstanding are not contacted.'
                : <>One email each asking for <strong className="text-ink">{chaseIntent.documentLabel}</strong>.</>}
            </p>
            <div className="bg-[#FBFCF6] border border-line rounded-xl px-3 py-2 mb-4 max-h-32 overflow-auto">
              {chaseIntent.rows.map(r => (
                <p key={r.supplierId} className="font-['Metropolis'] text-[11px] text-fusion-mid-grey truncate">{r.name}</p>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setChaseIntent(null)}
                className="bg-transparent border border-line rounded-xl text-[#51534A] text-xs font-semibold px-3.5 py-2 hover:text-ink hover:border-[#DADCCD] transition-colors">
                Cancel
              </button>
              <button type="button" disabled={chasing} onClick={() => runChase(chaseIntent)}
                className="bg-[#92D400] text-[#131313] rounded-xl text-xs font-bold px-3.5 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-50">
                {chasing ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SECTION 4: What it is costing ===== */}
      {/* §7 — the red tile says "cannot award these suppliers". This is where
          that stops being an abstraction: open briefs whose recipients include
          somebody who can't be awarded. */}
      {blockedBriefs.length > 0 && (
        <>
          {sectionHeader('Blocked on compliance', 'Open briefs with recipients who cannot be awarded', blockedBriefs.length, 'red')}
          <div className="bg-white border border-line rounded-xl overflow-hidden">
            {blockedBriefs.map(b => (
              <button
                key={b.id}
                type="button"
                onClick={() => navigate?.(`/briefs/${b.id}`)}
                className="w-full text-left flex items-center gap-4 px-4 py-3 border-b border-line last:border-b-0 hover:bg-[#F7F9F0]/60 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-fusion-forest">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-ink truncate">{b.name}</p>
                  <p className="text-[11px] text-[#51534A] mt-0.5 truncate">{b.blockedNames.join(' · ')}</p>
                </div>
                <span className="text-[10px] font-bold tracking-[1.5px] uppercase rounded-full px-2.5 py-1 bg-[#E0290A]/10 text-[#9c2b12] border border-[#E0290A]/30 whitespace-nowrap flex-shrink-0">
                  {b.blockedNames.length} of {b.recipientCount}
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* ===== SECTION 5: Recent Activity ===== */}
      {sectionHeader('Recent Activity', 'Full audit trail of compliance decisions')}
      {audit.length === 0 ? (
        <p className="text-sm text-[#51534A] italic py-4">No recent activity.</p>
      ) : (
        <div className="bg-white border border-line rounded-xl overflow-hidden">
          {audit.slice(0, 5).map(ev => {
            const supplierName = supplierById.get(ev.supplier_id)?.supplier_name || 'Unknown supplier';
            const docName = ev.document_key ? labelFor(ev.document_key) : null;
            const dotColor =
              ev.action === 'approved'       ? '#92D400' :
              ev.action === 'reminder_sent'  ? '#2E8FA1' :
              ev.action === 'override'       ? '#E89A2E' :
              ev.action === 'expired'        ? '#E0290A' :
              ev.action === 'rejected'       ? '#E0290A' :
              ev.action === 'needs_clarification' ? '#E89A2E' :
              ev.action === 'deleted'        ? '#51534A' :
              ev.action === 'archived'       ? '#51534A' : '#51534A';
            const actionLabel =
              ev.action === 'approved'       ? 'approved' :
              ev.action === 'reminder_sent'  ? (ev.meta?.ruleApplied === 'auto' ? 'auto-reminded' : 'sent reminder to') :
              ev.action === 'override'       ? 'overrode expiry on' :
              ev.action === 'expired'        ? 'document expired ·' :
              ev.action === 'rejected'       ? 'rejected a document for' :
              ev.action === 'needs_clarification' ? 'requested info from' :
              ev.action === 'deleted'        ? 'removed a document from' :
              ev.action === 'archived'       ? 'archived doc on' : ev.action;
            const actor = ev.actor?.full_name;
            // Subtitle context per action
            const subtitle = (() => {
              if (ev.action === 'approved') return ev.meta?.viewedFile ? 'Viewed file before approving' : 'Approved without preview';
              if (ev.action === 'reminder_sent') return ev.meta?.ruleApplied === 'auto' ? `Auto-reminder (${ev.meta?.isExpired ? 'post-expiry' : 'pre-expiry'})` : `Manual reminder (${ev.meta?.isExpired ? 'post-expiry' : 'pre-expiry'})`;
              if (ev.action === 'override') return ev.meta?.changedFrom && ev.meta?.changedTo ? `Changed from ${fmtDate(ev.meta.changedFrom, null) || 'not provided'} to ${fmtDate(ev.meta.changedTo)}${ev.meta.autoApproved ? ' · auto-approved' : ''}` : null;
              if (ev.action === 'expired')  return ev.meta?.ruleApplied === 'cron' ? 'Detected by daily expiry sweep' : null;
              if (ev.action === 'rejected' || ev.action === 'needs_clarification') return ev.meta?.notes || null;
              return null;
            })();
            return (
              <div key={ev.id} className="flex items-start gap-3 px-4 py-3 border-b border-line last:border-b-0">
                <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: dotColor }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-fusion-mid-grey">
                    {actor ? <span className="text-fusion-forest font-semibold">{actor}</span> : <span className="text-[#51534A]">System</span>}
                    {' '}{actionLabel}{' '}
                    <span className="text-ink font-semibold">{supplierName}</span>
                    {docName && <span className="text-[#51534A]"> — {docName}</span>}
                  </p>
                  {subtitle && <p className="text-[11px] text-[#51534A] mt-0.5">{subtitle}</p>}
                </div>
                <p className="text-[11px] text-[#51534A] flex-shrink-0 whitespace-nowrap">{fmtRelative(ev.ts)}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3">
        <button
          type="button"
          onClick={() => { /* TODO: full audit log page — out of scope for this build */ }}
          className="font-['Metropolis'] text-[10px] font-bold tracking-[2px] uppercase text-fusion-forest hover:opacity-85 transition-opacity">
          View full audit log →
        </button>
      </div>
    </div>
  );
}
